import brut from './donnees/demarrage-guide.v1.json' with { type: 'json' };
import { ECRANS } from '../../api/contrat.js';

/**
 * Catalogue du démarrage guidé (L64) : les 21 étapes d'un dossier de régularisation successorale, d'après les vocaux d'Isabelle du
 * 28/10. Donnée versionnée (`donnees/demarrage-guide.v1.json`), validée au chargement : un catalogue incohérent arrête l'écran.
 */
export type Intervenant = 'dirigeante' | 'assistante' | 'notaire';

export interface OptionQuestion {
  code: string;
  libelle: string;
  /** Consigne affichée dès que cette réponse est choisie. */
  consigne: string;
  /** Écran à ouvrir pour cette réponse, s'il diffère de celui de l'étape. */
  ecran?: string;
  aVerifier?: readonly string[];
  /** La mission du Cabinet s'arrête (relais à l'avocat partenaire). */
  finDeMission?: boolean;
  /** Aucune action de recherche ne peut être proposée (loi 2006-728, art. 36). */
  rechercheInterdite?: boolean;
}

/**
 * Condition de passage d'une étape (couloir G, point 4) : un libellé lisible, la façon de l'évaluer sur les vraies données du dossier
 * (« manuelle » : elle se coche à la main), et l'écran qui permet de la remplir tout de suite, avec le texte de son bouton.
 */
export type EvaluationCondition = 'mandat_recherche_signe' | 'personne_arbre' | 'defunt_renseigne' | 'manuelle';
export interface ConditionEtape {
  code: string;
  libelle: string;
  evaluation: EvaluationCondition;
  ecran?: string;
  bouton?: string;
}
const EVALUATIONS: ReadonlySet<string> = new Set(['mandat_recherche_signe', 'personne_arbre', 'defunt_renseigne', 'manuelle']);

export interface EtapeCatalogue {
  code: string;
  numero: number;
  phase: string;
  titre: string;
  qui: Intervenant;
  suiviPar?: Intervenant;
  /** Consigne en une ou deux phrases : ce qu'une débutante doit lire. */
  quoiFaire: string;
  details?: readonly string[];
  montants?: readonly { libelle: string; euros: number }[];
  aVerifier?: readonly string[];
  mentionEstimation?: boolean;
  /** Identifiant d'écran du contrat, jamais une adresse. */
  ecran: string;
  question?: { libelle: string; options: readonly OptionQuestion[] };
  conditions?: readonly ConditionEtape[];
}

export interface CatalogueDemarrage {
  version: string;
  statut: string;
  source: string;
  intervenants: Readonly<Record<Intervenant, string>>;
  /** Phrase montrée à qui consulte une étape faite par un autre intervenant (U9 du juge). */
  quandUnAutreFait: Readonly<Partial<Record<Intervenant, string>>>;
  phases: readonly { code: string; libelle: string }[];
  casParticulier: { code: string; libelle: string };
  etapes: readonly EtapeCatalogue[];
}

export function validerCatalogue(c: CatalogueDemarrage): CatalogueDemarrage {
  if (!/^\d{4}-\d{2}-\d{2}\.\d+$/.test(c.version)) throw new Error(`Version du démarrage guidé invalide : ${c.version}`);
  const ids = new Set<string>(ECRANS.map((e) => e.id));
  const phases = new Set([...c.phases.map((p) => p.code), c.casParticulier.code]);
  const codes = new Set<string>();
  c.etapes.forEach((e, i) => {
    if (codes.has(e.code)) throw new Error(`Étape en double dans le démarrage guidé : ${e.code}`);
    codes.add(e.code);
    if (e.numero !== i + 1) throw new Error(`Étape ${e.code} : numéro ${e.numero}, ${i + 1} attendu`);
    if (!ids.has(e.ecran)) throw new Error(`Étape ${e.code} : écran inconnu du contrat ${e.ecran}`);
    if (!phases.has(e.phase)) throw new Error(`Étape ${e.code} : phase inconnue ${e.phase}`);
    if (!(e.qui in c.intervenants)) throw new Error(`Étape ${e.code} : intervenant inconnu ${e.qui}`);
    for (const x of e.conditions ?? []) {
      if (!/^[a-z][a-z_]{1,48}$/.test(x.code)) throw new Error(`Étape ${e.code} : code de condition invalide ${x.code}`);
      if (!EVALUATIONS.has(x.evaluation)) throw new Error(`Étape ${e.code}, condition ${x.code} : évaluation inconnue ${x.evaluation}`);
      if (x.ecran && !ids.has(x.ecran)) throw new Error(`Étape ${e.code}, condition ${x.code} : écran inconnu du contrat ${x.ecran}`);
    }
    for (const o of e.question?.options ?? [])
      if (o.ecran && !ids.has(o.ecran)) throw new Error(`Étape ${e.code}, réponse ${o.code} : écran inconnu du contrat ${o.ecran}`);
  });
  return c;
}

export const CATALOGUE_DEMARRAGE: CatalogueDemarrage = validerCatalogue(brut as CatalogueDemarrage);
